import { EventEmitter } from './eventEmitter';
import { defaults as IndicatorModelDefaults } from '../model/IndicatorModel';
import { defaults as StakeholderModelDefaults } from '../model/StakeholderModel';
import { defaults as ScenarioModelDefaults } from '../model/ScenarioModel';
import { defaults as RiskModelDefaults } from '../model/RiskModel';

// Types d'actions supportées
export const ACTION_TYPES = [
	'startCanvas',
	'endCanvas',
	'setData',
	'image',
	'enableStakeholder',
	'disableStakeholder',
	'showScreen',
	'hideScreen',
	'createScenario',
	'enableScenario',
	'setScenario',
	'reverseSetScenario',
	'createIndicator',
	'enableIndicator',
	'createElement',
	'deleteElement',
	'createOpportunity',
	'updateOpportunity',
	'deleteOpportunity',
	'updateElement',
	'setNavigationEnabled',
	'reverseSetNavigationEnabled'
] as const;

export type ActionType = (typeof ACTION_TYPES)[number];

const defaultValues: Record<string, any> = {
	indicators: IndicatorModelDefaults,
	risks: RiskModelDefaults,
	stakeholders: StakeholderModelDefaults,
	scenarios: ScenarioModelDefaults
};

// Interface pour les paramètres des actions
export interface ActionParams {
	id?: string;
	name?: string;
	trend?: string;
	url?: string;
	screen?: string;
	data?: Record<string, any>;
	[key: string]: any;
}

// Interface pour une action complète
export interface RadarAction {
	type: ActionType;
	params: ActionParams;
}

const DEFAULT_STATE = {
	canvas: {
		visible: false,
		animated: false
	},
	stakeholders: {},
	indicators: {},
	scenarios: {},
	scenario: null,
	currentScreen: null,
	imageUrl: null,
	recentCreations: {}
};

/**
 * Gestionnaire central des actions radar
 * Utilise un système d'événements pour faciliter la communication entre composants
 */
class RadarActionManager extends EventEmitter {
	private static instance: RadarActionManager;
	private actionHandlers: Record<ActionType, (params: ActionParams) => void> =
		{} as any;
	private actions: RadarAction[] = [];
	private currentState: Record<string, any> = { ...DEFAULT_STATE };

	private constructor() {
		super();
		this.registerDefaultHandlers();
	}

	/**
	 * Obtenir l'instance singleton du gestionnaire
	 */
	public static getInstance(): RadarActionManager {
		if (!RadarActionManager.instance) {
			RadarActionManager.instance = new RadarActionManager();
		}
		return RadarActionManager.instance;
	}

	/**
	 * Enregistre les gestionnaires par défaut pour les types d'actions standard
	 */
	private registerDefaultHandlers(): void {
		// Canvas
		this.registerHandler('startCanvas', (params) => {
			this.updateState('canvas', { visible: true, animated: true });
			this.emit('canvas:start', params);
		});

		this.registerHandler('endCanvas', (params) => {
			this.updateState('canvas', { visible: false, animated: false });
			this.emit('canvas:end', params);
		});

		this.registerHandler('updateElement', (params) => {
			if (!params.id) return;
			this.updateElement(params.type, params.id, {
				...params
			});

			this.emit('element:update', params);
		});

		this.registerHandler('setScenario', (params) => {
			console.log('setScenario', params);
			this.updateState('scenario', params.to);
			this.emit('scenario:set', params);
		});

		this.registerHandler('reverseSetScenario', (params) => {
			this.updateState('scenario', params.from);
			this.emit('scenario:set', params);
		});

		this.registerHandler('reverseSetNavigationEnabled', (params) => {
			console.log('reverseSetNavigationEnabled', params);
			this.updateState(
				'isNavigationEnabled',
				params.enabled && params.enabled !== 'false' ? false : true
			);
			this.emit('navigation:enable', params);
		});

		// Affichage
		this.registerHandler('showScreen', (params) => {
			if (!params.to) return;
			this.updateState('currentScreen', params.to);
			this.emit('screen:change', params);
		});

		this.registerHandler('hideScreen', (params) => {
			this.updateState('currentScreen', params.from || null);
			this.emit('screen:hide', params);
		});

		this.registerHandler('image', (params) => {
			if (!params.url) return;
			this.updateState('imageUrl', params.url);
			this.emit('image:show', params);
		});

		this.registerHandler('setData', (params) => {
			if (!params.data) return;
			this.emit('data:set', params.data);
		});

		this.registerHandler('createOpportunity', (params) => {
			if (!params.id) return;

			const finalParams = {
				...defaultValues[params.type], // Valeurs par défaut du modèle
				...params,
				enabled: true,
				isOpportunity: true
			};

			this.addToElements('risks', finalParams);
		});

		this.registerHandler('updateOpportunity', (params) => {
			if (!params.id) return;

			this.updateElement('risks', params.id, params);
		});

		this.registerHandler('createElement', (params) => {
			if (!params.id) return;

			// Fusion des valeurs par défaut et des paramètres fournis
			const finalParams = {
				...defaultValues[params.type], // Valeurs par défaut du modèle
				...params,
				enabled: true
			};

			this.addToElements(params.type, finalParams);
		});

		this.registerHandler('deleteOpportunity', (params) => {
			if (!params.id) return;

			this.removeFromElements('risks', params.id);
		});

		this.registerHandler('deleteElement', (params) => {
			if (!params.id) return;

			this.removeFromElements(params.type, params.id);
		});

		this.registerHandler('setNavigationEnabled', (params) => {
			this.updateState('isNavigationEnabled', params.enabled === 'true');
			this.emit('navigation:enabled', {
				enabled: params.enabled === 'true'
			});
		});
	}

	/**
	 * Enregistre un gestionnaire personnalisé pour un type d'action
	 */
	public registerHandler(
		actionType: ActionType,
		handler: (params: ActionParams) => void
	): void {
		this.actionHandlers[actionType] = handler;
	}

	/**
	 * Exécute une action spécifique
	 */
	public executeAction(action: RadarAction): void {
		this.actions.push(action);
		let handler = this.actionHandlers[action.type];

		// Gérer les actions créatives (createStakeholder, createIndicator, etc.)
		if (action.type.startsWith('create') && !handler) {
			const type = (
				action.type.replace('create', '') + 's'
			).toLowerCase();

			handler = this.actionHandlers['createElement'];
			action.params.type = type;
		}

		if (action.type.startsWith('delete') && !handler) {
			const type = (
				action.type.replace('delete', '') + 's'
			).toLowerCase();

			handler = this.actionHandlers['deleteElement'];
			action.params.type = type;
		}

		if (action.type.startsWith('update') && !handler) {
			const type = (
				action.type.replace('update', '') + 's'
			).toLowerCase();

			handler = this.actionHandlers['updateElement'];
			action.params.type = type;
		}

		if (handler) {
			// Conserver une copie des paramètres pour le debugging
			const paramsCopy = { ...action.params };

			handler(paramsCopy);
			this.emit('action:executed', action);

			if (action.params.debug) {
				console.debug(action.params.debug);
			}

			return;
		} else {
			console.warn(
				`No handler registered for action type: ${action.type}`
			);
		}
	}

	/**
	 * Mise à jour de l'état d'un élément
	 */
	private updateElement(
		type: string,
		id: string,
		updates: Record<string, any>
	): void {
		console.group('updateElement');
		console.log('updateElement', type, id, updates);

		if (!this.currentState[type]) return;
		const item = this.currentState[type][id] || {};

		// Not in a scenario
		if (!this.currentState.scenario) {
			this.currentState[type][id] = {
				...item,
				...updates
			};
		} else {
			const existingItemScenarioDetailsIndex = (
				item.scenarios || []
			).findIndex(
				(s: { scenarioId: string }) =>
					s.scenarioId === this.currentState.scenario
			);

			console.log(
				'existingItemScenarioDetailsIndex',
				existingItemScenarioDetailsIndex
			);

			let existingItemScenarioDetails =
				existingItemScenarioDetailsIndex > -1
					? item.scenarios[existingItemScenarioDetailsIndex]
					: {
							id: item.id + '-' + this.currentState.scenario,
							scenarioId: this.currentState.scenario
						};

			console.log('existingItemScenarioDetails', {
				...existingItemScenarioDetails
			});

			existingItemScenarioDetails = {
				...existingItemScenarioDetails,
				...updates
			};

			console.log(
				'existingItemScenarioDetails after update',
				existingItemScenarioDetails
			);

			console.log('current scenario', this.currentState.scenario);

			item.scenarios = (item.scenarios || []).map(
				(s: { scenarioId: string }) =>
					s.scenarioId === this.currentState.scenario
						? existingItemScenarioDetails
						: s
			);

			if (existingItemScenarioDetailsIndex == -1) {
				item.scenarios = [
					...item.scenarios,
					existingItemScenarioDetails
				];
			}

			this.currentState[type][id] = {
				...item,
				scenarios: [...item.scenarios]
			};

			console.log(
				'currentState item after update',
				JSON.parse(JSON.stringify(this.currentState[type][id]))
			);

			this.emit(
				`${type.replace(/s$/, '')}:update`,
				this.currentState[type][id]
			);
		}
		console.groupEnd();
	}

	private addToElements(elementType: string, params: ActionParams): void {
		if (!params.id) return;

		// S'assurer que le nom et le trend sont conservés
		const id = params.id || '';
		const name = params.name || '';

		console.log(
			`Ajout à ${elementType} - ID: ${params.id}, Nom: "${name}"`
		);

		// S'assurer que la collection existe
		if (!this.currentState[elementType]) {
			console.log(`Initialisation de la collection ${elementType}`);
			this.currentState[elementType] = {};
		}

		// Ajouter l'élément
		this.currentState[elementType][params.id] = {
			...params,
			enabled: true
			// Forcer l'activation par défaut
		};
		this.currentState.recentCreations[id] = true;

		setTimeout(() => {
			delete this.currentState.recentCreations[id];

			this.updateState('recentCreations', {
				...this.currentState.recentCreations
			});
		}, 2000);

		// Log pour vérifier l'état
		console.log(`État après ajout:`, {
			elements: Object.keys(this.currentState[elementType]).length,
			newElement: this.currentState[elementType][params.id]
		});

		// Émettre un événement pour notifier de la création
		this.emit(`${elementType.replace(/s$/, '')}:create`, {
			id: params.id,
			name: name,
			enabled: true,
			...params
		});
	}

	private removeFromElements(elementType: string, id: string): void {
		if (!this.currentState[elementType]) return;
		delete this.currentState[elementType][id];
		this.emit(`${elementType.replace(/s$/, '')}:delete`, {
			id: id
		});
	}

	/**
	 * Mise à jour générique de l'état
	 */
	private updateState(key: string, value: any): void {
		this.currentState[key] = value;
		this.emit('state:update', { key, value });
	}

	/**
	 * Obtenir l'état actuel
	 */
	public getState(): Record<string, any> {
		return { ...this.currentState };
	}

	/**
	 * Obtenir les stakeholders activés
	 */
	public getEnabledStakeholders(): any[] {
		return Object.values(this.currentState.stakeholders).filter(
			(s: any) => s.enabled
		);
	}

	/**
	 * Obtenir le scénario actuel
	 */
	public getCurrentScenario(): any {
		const id = this.currentState.scenario;
		return id ? this.currentState.scenarios[id] : null;
	}

	/**
	 * Réinitialise complètement l'état
	 */
	public resetState(): void {
		this.currentState = { ...DEFAULT_STATE };
		this.actions = [];
		this.emit('state:reset');
	}

	/**
	 * Extraire les paramètres à partir d'un élément du DOM
	 */
	public static extractParamsFromElement(element: HTMLElement): ActionParams {
		const params: ActionParams = {};

		// Parcourir tous les attributs data-* et les extraire comme paramètres
		Array.from(element.attributes)
			.filter(
				(attr) =>
					attr.name.startsWith('data-') && attr.name !== 'data-action'
			)
			.forEach((attr) => {
				const paramName = attr.name.replace('data-', '');
				params[paramName] = attr.value;
			});

		return params;
	}

	/**
	 * Parser le texte d'une action au format [action param=value]
	 */
	public static parseActionText(actionText: string): RadarAction | null {
		// Ajouter des logs de débogage
		console.log(`🔄 Parsing de l'action: "${actionText}"`);

		try {
			// Vérifier si c'est au format JSON (#[...]#)
			if (actionText.startsWith('#[') && actionText.endsWith(']#')) {
				try {
					// Extraire et parser le JSON
					const jsonText = actionText.substring(
						2,
						actionText.length - 2
					);
					const data = JSON.parse(jsonText);

					return {
						type: 'setData',
						params: { data }
					};
				} catch (err) {
					console.error('Erreur de parsing JSON:', err);
					return null;
				}
			}

			// Format standard [action param=value]
			// Extraction du type d'action (premier mot)
			const firstSpaceIndex = actionText.indexOf(' ');
			const actionType =
				firstSpaceIndex > -1
					? (actionText
							.substring(0, firstSpaceIndex)
							.trim() as ActionType)
					: (actionText.trim() as ActionType);

			// Vérification plus détaillée pour startCanvas
			if (actionType === 'startCanvas') {
				console.log(`✅ Action startCanvas détectée!`);
			}

			if (!ACTION_TYPES.includes(actionType)) {
				console.warn(
					`⚠️ Type d'action non reconnu: "${actionType}" (action complète: "${actionText}")`
				);
				console.log(
					`📝 Types d'actions valides: ${ACTION_TYPES.join(', ')}`
				);
				return null;
			}

			const params: ActionParams = {};

			// Nouvelle méthode pour extraire les paramètres qui prend en charge les espaces dans les valeurs
			if (firstSpaceIndex > -1) {
				const paramsText = actionText
					.substring(firstSpaceIndex + 1)
					.trim();

				// Utiliser une expression régulière qui gère les valeurs entre guillemets
				const paramRegex = /(\w+)=(?:"([^"]*)"|'([^']*)'|([^\s"']+))/g;
				let match;

				while ((match = paramRegex.exec(paramsText)) !== null) {
					const key = match[1];
					// La valeur peut être dans le groupe 2 (guillemets doubles), 3 (guillemets simples) ou 4 (sans guillemets)
					const value = match[2] || match[3] || match[4];
					params[key] = value;
				}
			}

			// Log de l'action parsée avec succès
			console.log(
				`✅ Action parsée: type=${actionType}, params=`,
				params
			);

			return {
				type: actionType,
				params
			};
		} catch (err) {
			console.error(
				"❌ Erreur lors du parsing de l'action:",
				err,
				'Texte:',
				actionText
			);
			return null;
		}
	}

	/**
	 * Préparer le contenu en remplaçant les balises #[...]# par des actions
	 * Utilisé pour la compatibilité avec l'ancien format
	 */
	public static prepareContentWithLegacyActions(content: string): string {
		// Remplacer les balises JSON par des balises d'action standards
		return content.replace(/#(\[[\s|\S]*?\])#/g, (match, jsonContent) => {
			try {
				const data = JSON.parse(jsonContent);
				// Convertir en une série de balises d'action standards
				let replacementContent = '';

				if (Array.isArray(data)) {
					// Gérer le cas d'un tableau de stakeholders
					data.forEach((item, index) => {
						if (item.id) {
							replacementContent += `[enableStakeholder id="${item.id}"]`;

							if (item.trend) {
								replacementContent += `[updateStakeholder id="${item.id}" trend="${item.trend}"]`;
							}
						}
					});
				} else if (typeof data === 'object') {
					// Gérer un objet de données
					replacementContent = `[setData data="${encodeURIComponent(JSON.stringify(data))}"]`;
				}

				return replacementContent || match;
			} catch (error) {
				console.error(
					'Erreur de parsing JSON:',
					error,
					'JSON brut:',
					jsonContent
				);
				return match;
			}
		});
	}
}

export default RadarActionManager;
